import React, { useCallback, useEffect, useRef, useState } from 'react';
import { portalDiktatSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';
import {
  Headphones,
  Upload,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Smartphone,
  Link2,
  Mic,
  FileCheck,
  FileText,
  Download,
  Copy,
  Square,
  RotateCcw,
  FolderOpen,
  Info,
} from 'lucide-react';
import { FEATURE_DIKTAT } from '../../../constants';
import type { DiktatTab } from '../portalNavigation';
import { PORTAL_SHELL } from '../portalTheme';
import {
  ELEMENT_DOWNLOAD_URL,
  ElementRoomEmbed,
  portalMatrixHomeserverUrl,
} from './MatrixView';
import type { DiktatArchiveEntry, DiktatDokumenteCtaKind, DiktatOutputFormat, ElementDiktatLoadAssessment } from '../portalDiktatFlow';
import {
  DIKTAT_ELEMENT_DND_HINT,
  DIKTAT_ELEMENT_SESSION_LOCK_HINT,
  DIKTAT_NOT_IN_AKTE_HINT,
  diktatArchiveSourceLabel,
  diktatDropLooksLikeAudio,
  diktatEditAction,
  diktatExplorerRowGate,
  diktatHasAllowedEditPath,
  elementEmbedSrcForDiktat,
  protokollFromDiktatArchiveEntry,
} from '../portalDiktatFlow';
import { DiktatOutputFormatMenu } from '../DiktatOutputFormatMenu';
import { usePopoverDismissOutside } from '../usePopoverDismissOutside';
import { DokumenteRowActions } from './DokumenteRowActions';
import { PortalDocumentSharePanel } from '../PortalDocumentSharePanel';
import type { KanzleiSeatForAktePicker, PortalAkteRecord } from '../portalAktenTypes';
import type { PortalProtokoll, PortalDocumentOpenFields } from '../portalDocuments';
import {
  archivePortalDocumentRecord,
  canOpenDocumentInPortal,
  createPortalFolderViaApi,
  movePortalDocumentViaFolderApi,
  portalDocumentOpenFieldsFromProtokoll,
} from '../portalDocuments';
import {
  listKanzleiSeatsForAktePicker,
  updatePortalAkteNcPath,
} from '../portalAkten';
import { portalAkteAssignTargetFolder, portalAkteNcPathFromMovedDocument } from '../portalAkteDocumentActions';
import {
  listPortalDocumentShares,
  placeSharePopover,
  type PortalDocumentShare,
} from '../portalDocumentShares';

export interface DiktatViewProps {
  activeTab: DiktatTab;
  onTabChange: (tab: DiktatTab) => void;
  onOpenChat: () => void;
  diktatFile: File | null;
  onDiktatFileChange: (file: File | null) => void;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
  diktatResult: {
    type: 'success' | 'error';
    pdfUrl?: string;
    message?: string;
    pb_record_id?: string;
    transcriptText?: string;
    auswertungText?: string;
    analysisBlockedReason?: string;
  } | null;
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  dokumenteCta?: DiktatDokumenteCtaKind;
  archive?: DiktatArchiveEntry[];
  selectedArchiveId?: string | null;
  onSelectArchive?: (id: string) => void;
  protokolle?: PortalProtokoll[];
  akten?: PortalAkteRecord[];
  canWriteDocuments?: boolean;
  isPremium?: boolean;
  authToken?: string | null;
  customerId?: string;
  kanzleiId?: string;
  customerStatus?: string;
  customerEmail?: string;
  protokolleLoading?: boolean;
  onRefreshDocuments?: () => void | Promise<void>;
  onOpenDocument?: (fields: PortalDocumentOpenFields) => void;
  onChooseOutputFormat?: (format: DiktatOutputFormat, source: 'result' | string) => void;
  formatBusy?: boolean;
  formatError?: string | null;
  /** PB-Feld matrixRoomId — Raum-Link im Reiter Handy/Element. */
  matrixRoomId?: string;
  elementLoad: ElementDiktatLoadAssessment;
  analysisSlot?: React.ReactNode;
  /** Datei aufs Diktat-Fenster → Whisper (kein Fake-Phone-Sync). */
  onDiktatDroppedAudio?: (file: File) => void;
  /** Gemerkte offene Akte, sonst leer. Kein erfundener Eintrag. */
  intoAkteId?: string;
  onIntoAkte?: () => void;
  intoAkteBusy?: boolean;
  intoAkteNote?: string | null;
}

const DIKTAT_TABS: Array<{ id: DiktatTab; label: string; hint: string; Icon: typeof Mic }> = [
  { id: 'record', label: 'Aufnehmen', hint: 'Mikrofon', Icon: Mic },
  { id: 'upload', label: 'Hochladen', hint: 'Hochladen', Icon: Upload },
  { id: 'anleitung', label: 'Handy / Element', hint: 'Element', Icon: Smartphone },
];

/** Schwarzer Grund, feine lila Kante — wie die Aktenliste. */
const DIKTAT_GROUND = 'bg-[#07070a]';
const DIKTAT_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
/** Knopf wie Neue Akte und Öffnen. */
const DIKTAT_BTN =
  'portal-diktat-standard inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45';
/** Zeile im Verschieben-Dialog: schwarzer Grund, feine Kante, Name lesbar. */
const DIKTAT_QUIET =
  'flex w-full items-center rounded-md border border-violet-400/30 bg-[#07070a] px-3 py-2 text-left text-xs text-slate-100 hover:border-violet-400/60 disabled:cursor-not-allowed disabled:opacity-45';
/** Icon-Knopf wie die Leiste im Hauptreiter. */
const DIKTAT_ICON =
  'portal-diktat-icon inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45';
const DIKTAT_ICON_ON =
  'portal-diktat-icon-on inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400 bg-violet-600 text-white disabled:cursor-not-allowed disabled:opacity-45';

const DIKTAT_PANE_ERROR =
  'Das Diktat zeigt seinen Kopf. Die Fläche konnte gerade nicht gezeichnet werden.';

/**
 * Dieselbe Würfel-Drehung wie Arko ON/off: rotateX, 500ms, Perspektive 280.
 * Nur wo etwas auf- und zuklappt.
 */
function DiktatCube({
  open,
  front,
  back,
}: {
  open: boolean;
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md [backface-visibility:hidden]';
  return (
    <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={`${face} border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span className={`${face} bg-violet-600 text-white`} style={{ transform: 'rotateX(180deg)' }}>
          {back}
        </span>
      </span>
    </span>
  );
}

const DiktatRailButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    hint: string;
    pressed?: boolean;
    chrome?: boolean;
    hintAlign?: 'center' | 'end';
  }
>(function DiktatRailButton({ hint, pressed, chrome = true, hintAlign = 'center', className = '', children, ...rest }, ref) {
  const face = chrome ? (pressed ? DIKTAT_ICON_ON : DIKTAT_ICON) : '';
  const hintPlace = hintAlign === 'end' ? 'right-0 left-auto translate-x-0' : 'left-1/2 -translate-x-1/2';
  return (
    <button
      {...rest}
      ref={ref}
      className={`group/rail relative h-7 w-7 shrink-0 origin-center p-0 transition-transform duration-300 ease-out hover:z-20 hover:scale-125 focus-visible:z-20 focus-visible:scale-125 ${face} ${className}`}
    >
      {children}
      <span className={`pointer-events-none absolute top-[calc(100%+4px)] z-30 max-w-none overflow-visible whitespace-nowrap ${hintPlace} rounded border border-violet-400/40 bg-[#07070a] px-1.5 py-0.5 text-[10px] font-medium text-violet-100 opacity-0 transition-opacity duration-300 ease-out group-hover/rail:opacity-100 group-focus-visible/rail:opacity-100`}>
        {hint}
      </span>
    </button>
  );
});

function DiktatKopf({
  activeTab,
  onTabChange,
  trailing,
}: {
  activeTab: DiktatTab;
  onTabChange?: (tab: DiktatTab) => void;
  trailing?: React.ReactNode;
}) {
  return (
    <header className={`relative shrink-0 space-y-3 px-3.5 py-3 ${DIKTAT_EDGE}`}>
      <h1 className="m-0 text-sm font-semibold text-white">{FEATURE_DIKTAT}</h1>
      <p className="m-0 mt-0.5 pr-10 text-[11px] text-slate-500">
        Direkt im Browser aufnehmen oder Audio aus Element laden — Transkript sichtbar, danach Format wählen und weiterarbeiten.
      </p>
      <div className="flex items-center gap-1" role="tablist" aria-label="Diktat">
        {DIKTAT_TABS.map((tab) => {
          const TabIcon = tab.Icon;
          const on = activeTab === tab.id;
          return (
            <DiktatRailButton
              key={tab.id}
              type="button"
              role="tab"
              hint={tab.hint}
              pressed={on}
              aria-selected={on}
              aria-label={tab.hint}
              data-testid={`portal-diktat-tab-${tab.id}`}
              onClick={onTabChange ? () => onTabChange(tab.id) : undefined}
            >
              <TabIcon className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">{tab.label}</span>
            </DiktatRailButton>
          );
        })}
      </div>
      {trailing ? <div className="absolute right-2 top-2 z-30">{trailing}</div> : null}
    </header>
  );
}

function pickRecorderMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4', ''];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  for (const c of candidates) {
    if (c === '' || MediaRecorder.isTypeSupported(c)) return c;
  }
  return '';
}

function extensionForMime(mime: string): string {
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4') || mime.includes('m4a') || mime.includes('aac')) return 'm4a';
  return 'webm';
}

function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function pickDiktatDropFile(list: FileList | null | undefined): File | null {
  const files = list ? Array.from(list) : [];
  for (const file of files) {
    if (diktatDropLooksLikeAudio(file.name, file.type)) return file;
  }
  return null;
}

function diktatAudioDropHandlers(
  onFile: (file: File) => void,
  disabled: boolean,
): {
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
} {
  return {
    onDragOver: (e) => {
      if (disabled) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDrop: (e) => {
      if (disabled) return;
      e.preventDefault();
      const file = pickDiktatDropFile(e.dataTransfer.files);
      if (file) onFile(file);
    },
  };
}

export const DiktatView: React.FC<DiktatViewProps> = ({
  activeTab,
  onTabChange,
  onOpenChat,
  diktatFile,
  onDiktatFileChange,
  diktatUploading,
  onDiktatUpload,
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  dokumenteCta = 'honest_not_in_akte',
  archive = [],
  selectedArchiveId = null,
  onSelectArchive,
  protokolle = [],
  akten = [],
  canWriteDocuments = false,
  isPremium = false,
  authToken = null,
  customerId = '',
  kanzleiId = '',
  customerStatus,
  customerEmail,
  protokolleLoading = false,
  onRefreshDocuments,
  onOpenDocument,
  onChooseOutputFormat,
  formatBusy = false,
  formatError = null,
  matrixRoomId = '',
  elementLoad,
  analysisSlot,
  onDiktatDroppedAudio,
  intoAkteId = '',
  onIntoAkte,
  intoAkteBusy = false,
  intoAkteNote = null,
}) => {
  const [formatMenuKey, setFormatMenuKey] = useState<string | null>(null);
  const acceptDroppedAudio = useCallback(
    (file: File) => {
      if (onDiktatDroppedAudio) onDiktatDroppedAudio(file);
      else onDiktatFileChange(file);
    },
    [onDiktatDroppedAudio, onDiktatFileChange],
  );
  const dropHandlers = diktatAudioDropHandlers(acceptDroppedAudio, diktatUploading);
  const formatProps = {
    onChooseOutputFormat,
    formatBusy,
    formatError,
  formatMenuOpen: formatMenuKey === 'result',
  onFormatMenuOpenChange: (open: boolean) => setFormatMenuKey(open ? 'result' : null),
  intoAkteId,
  onIntoAkte,
  intoAkteBusy,
  intoAkteNote,
};
  const archiveFolder = (
    <DiktatArchiveFolder
      archive={archive}
      selectedId={selectedArchiveId}
      onSelect={onSelectArchive}
      protokolle={protokolle}
      akten={akten}
      canWriteDocuments={canWriteDocuments}
      isPremium={isPremium}
      authToken={authToken}
      customerId={customerId}
      kanzleiId={kanzleiId}
      matrixRoomId={matrixRoomId}
      customerStatus={customerStatus}
      customerEmail={customerEmail}
      protokolleLoading={protokolleLoading}
      onRefreshDocuments={onRefreshDocuments}
      onOpenDocument={onOpenDocument}
      onChooseOutputFormat={onChooseOutputFormat}
      formatBusy={formatBusy}
      formatError={formatError}
      formatMenuKey={formatMenuKey}
      onFormatMenuKeyChange={setFormatMenuKey}
    />
  );
  const analysisBlock = analysisSlot ? (
    <div className="pt-1" data-testid="portal-diktat-analysis-slot">
      {analysisSlot}
    </div>
  ) : null;

  const diktatLight = portalDiktatSurfaceClass(usePortalAppearance());

  return (
  <div
    className={`@container w-full min-w-0 max-w-none h-full min-h-0 flex flex-1 flex-col overflow-hidden gap-3 px-3 py-3 ${DIKTAT_GROUND} ${diktatLight}`}
    data-testid="portal-diktat-dropzone"
    data-layout="pane-fit"
    data-diktat-pane="scroll-or-grow"
    {...dropHandlers}
  >
    <DiktatKopf
      activeTab={activeTab}
      onTabChange={onTabChange}
      trailing={<DiktatElementHowtoPopover onOpenChat={onOpenChat} />}
    />

    {activeTab === 'anleitung' ? (
      <DiktatAnleitungPanel
        matrixRoomId={matrixRoomId}
        elementLoad={elementLoad}
        diktatFile={diktatFile}
        onDiktatFileChange={onDiktatFileChange}
        diktatUploading={diktatUploading}
        onDiktatUpload={onDiktatUpload}
        diktatResult={diktatResult}
        onDiktatResultClear={onDiktatResultClear}
        onShowInDokumente={onShowInDokumente}
        dokumenteCta={dokumenteCta}
        side={
          <>
            {analysisBlock}
            {archiveFolder}
          </>
        }
        {...formatProps}
      />
    ) : (
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-6">
        {activeTab === 'record' ? (
          <DiktatRecordPanel
            diktatFile={diktatFile}
            onDiktatFileChange={onDiktatFileChange}
            diktatUploading={diktatUploading}
            onDiktatUpload={onDiktatUpload}
            diktatResult={diktatResult}
            onDiktatResultClear={onDiktatResultClear}
            onShowInDokumente={onShowInDokumente}
            dokumenteCta={dokumenteCta}
            onShowUpload={() => onTabChange('upload')}
            onShowAnleitung={() => onTabChange('anleitung')}
            {...formatProps}
          />
        ) : null}
        {activeTab === 'upload' ? (
          <DiktatUploadPanel
            diktatFile={diktatFile}
            onDiktatFileChange={onDiktatFileChange}
            diktatUploading={diktatUploading}
            onDiktatUpload={onDiktatUpload}
            diktatResult={diktatResult}
            onDiktatResultClear={onDiktatResultClear}
            onShowInDokumente={onShowInDokumente}
            dokumenteCta={dokumenteCta}
            onShowAnleitung={() => onTabChange('anleitung')}
            onDroppedAudio={acceptDroppedAudio}
            {...formatProps}
          />
        ) : null}
        {analysisBlock}
        {archiveFolder}
      </div>
    )}
  </div>
  );
};

type DiktatFormatMenuProps = {
  onChooseOutputFormat?: (format: DiktatOutputFormat, source: 'result' | string) => void;
  formatBusy?: boolean;
  formatError?: string | null;
  formatMenuOpen?: boolean;
  onFormatMenuOpenChange?: (open: boolean) => void;
  intoAkteId?: string;
  onIntoAkte?: () => void;
  intoAkteBusy?: boolean;
  intoAkteNote?: string | null;
};

function DiktatCopyBlock({
  label,
  text,
  testId,
}: {
  label: string;
  text: string;
  testId: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <button
          type="button"
          data-testid={`${testId}-copy`}
          className={DIKTAT_ICON}
          aria-label={copied ? 'Kopiert' : 'Kopieren'}
          title={copied ? 'Kopiert' : 'Kopieren'}
          onClick={() => {
            const write =
              typeof navigator !== 'undefined' && navigator.clipboard
                ? navigator.clipboard.writeText(text)
                : null;
            if (!write) return;
            void write
              .then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1600);
              })
              .catch(() => {});
          }}
        >
          <Copy className="w-3.5 h-3.5" aria-hidden />
        </button>
      </div>
      <pre
        className={`m-0 max-h-64 overflow-y-auto custom-scrollbar whitespace-pre-wrap px-3 py-2 text-[13px] leading-relaxed text-slate-100 select-text ${DIKTAT_EDGE}`}
        data-testid={testId}
      >
        {text}
      </pre>
    </div>
  );
}

const DiktatResultBlocks: React.FC<{
  diktatResult: DiktatViewProps['diktatResult'];
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  dokumenteCta: DiktatDokumenteCtaKind;
} & DiktatFormatMenuProps> = ({
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  dokumenteCta,
  onChooseOutputFormat,
  formatBusy = false,
  formatError = null,
  formatMenuOpen = false,
  onFormatMenuOpenChange,
  intoAkteId = '',
  onIntoAkte,
  intoAkteBusy = false,
  intoAkteNote = null,
}) => (
  <>
    {diktatResult?.type === 'success' && (
      <div className={`p-3 space-y-3 ${DIKTAT_EDGE}`}>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-emerald-400 font-semibold text-sm">
              {diktatResult.transcriptText ? 'Transkript bereit' : 'Datei angekommen'}
            </p>
            {diktatResult.message && <p className="text-slate-300 text-sm mt-1">{diktatResult.message}</p>}
            {!diktatResult.pdfUrl && !diktatResult.transcriptText && (
              <p className="text-slate-400 text-sm mt-1">Kein Transkript in der Antwort — nichts unter Dokumente vorgetäuscht.</p>
            )}
          </div>
        </div>
        {diktatResult.transcriptText ? (
          <DiktatCopyBlock
            label="Transkript"
            text={diktatResult.transcriptText}
            testId="portal-diktat-transcript"
          />
        ) : null}
        {diktatResult.auswertungText ? (
          <DiktatCopyBlock
            label="Auswertung"
            text={diktatResult.auswertungText}
            testId="portal-diktat-auswertung"
          />
        ) : null}
        {diktatResult.analysisBlockedReason ? (
          <div
            className="flex items-start gap-2 text-sm text-amber-200/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2"
            data-testid="portal-diktat-analysis-blocked"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{diktatResult.analysisBlockedReason}</span>
          </div>
        ) : null}
        {diktatResult.transcriptText && intoAkteId && onIntoAkte ? (
          <div className="flex flex-col items-start gap-1">
            <button
              type="button"
              data-testid="portal-diktat-into-akte"
              className={DIKTAT_BTN}
              disabled={intoAkteBusy}
              onClick={onIntoAkte}
            >
              {intoAkteBusy ? 'Legt in die Akte…' : 'In diese Akte'}
            </button>
            {intoAkteNote ? (
              <p className="m-0 text-[11px] text-slate-400" data-testid="portal-diktat-into-akte-note">
                {intoAkteNote}
              </p>
            ) : null}
          </div>
        ) : null}
        {diktatResult.transcriptText && onChooseOutputFormat ? (
          <DiktatOutputFormatMenu
            open={formatMenuOpen}
            busy={formatBusy}
            error={formatError}
            onOpenChange={(open) => onFormatMenuOpenChange?.(open)}
            onChoose={(format) => onChooseOutputFormat(format, 'result')}
          />
        ) : null}
        {diktatResult.pdfUrl && (
          <a
            href={diktatResult.pdfUrl}
            target="_blank"
            rel="noreferrer"
            className={DIKTAT_ICON}
            title="Protokoll-PDF"
            aria-label="Protokoll-PDF"
          >
            <Download className="w-3.5 h-3.5" aria-hidden />
          </a>
        )}
        {dokumenteCta === 'show_in_explorer' && onShowInDokumente ? (
          <button
            type="button"
            onClick={() => onShowInDokumente(diktatResult.pb_record_id)}
            className={DIKTAT_ICON}
            title="In Dokumente anzeigen"
            aria-label="In Dokumente anzeigen"
            data-testid="portal-diktat-dokumente-cta"
          >
            <FileCheck className="w-3.5 h-3.5" aria-hidden />
          </button>
        ) : (
          <p
            className={`${PORTAL_SHELL.textMuted} text-xs leading-relaxed`}
            data-testid="portal-diktat-not-in-akte"
          >
            {DIKTAT_NOT_IN_AKTE_HINT}.
          </p>
        )}
        <button
          type="button"
          onClick={onDiktatResultClear}
          className={DIKTAT_BTN}
        >
          Weiteres Diktat
        </button>
      </div>
    )}

    {diktatResult?.type === 'error' && (
      <div className={`p-3 flex items-start gap-3 ${DIKTAT_EDGE}`} role="alert">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-red-400 font-semibold text-sm">Upload fehlgeschlagen</p>
          <p className="text-slate-400 text-sm mt-1">
            {diktatResult.message?.trim() || (
              <>
                Bitte erneut versuchen oder{' '}
                <a href="mailto:office@arkorbit.at" className="text-violet-300">
                  office@arkorbit.at
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    )}
  </>
);

const DiktatUploadAction: React.FC<{
  diktatFile: File | null;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
}> = ({ diktatFile, diktatUploading, onDiktatUpload }) => (
  <button
    type="button"
    onClick={onDiktatUpload}
    disabled={!diktatFile || diktatUploading}
    className={DIKTAT_ICON}
    title={diktatUploading ? 'Wird verarbeitet' : 'Diktat übermitteln'}
    aria-label={diktatUploading ? 'Wird verarbeitet' : 'Diktat übermitteln'}
  >
    {diktatUploading ? (
      <>
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
      </>
    ) : (
      <>
        <Upload className="w-3.5 h-3.5" aria-hidden />
      </>
    )}
  </button>
);

const PrivacyHint: React.FC = () => (
  <div className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
    <ShieldCheck className="w-4 h-4 text-emerald-500/80 flex-shrink-0 mt-0.5" />
    Verarbeitung in Frankfurt (EU). Transkription ohne Drittanbieter-Cloud.
  </div>
);

type RecordPhase = 'idle' | 'recording' | 'preview';

const DiktatRecordPanel: React.FC<{
  diktatFile: File | null;
  onDiktatFileChange: (file: File | null) => void;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
  diktatResult: DiktatViewProps['diktatResult'];
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  dokumenteCta: DiktatDokumenteCtaKind;
  onShowUpload: () => void;
  onShowAnleitung: () => void;
} & DiktatFormatMenuProps> = ({
  diktatFile,
  onDiktatFileChange,
  diktatUploading,
  onDiktatUpload,
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  dokumenteCta,
  onChooseOutputFormat,
  formatBusy,
  formatError,
  formatMenuOpen,
  onFormatMenuOpenChange,
  intoAkteId,
  onIntoAkte,
  intoAkteBusy,
  intoAkteNote,
}) => {
  const [phase, setPhase] = useState<RecordPhase>(diktatFile ? 'preview' : 'idle');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [micError, setMicError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<number | null>(null);
  const mimeRef = useRef('');

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const revokePreview = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const resetRecording = useCallback(() => {
    clearTimer();
    stopTracks();
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setElapsedSec(0);
    setMicError(null);
    revokePreview();
    onDiktatFileChange(null);
    setPhase('idle');
  }, [clearTimer, stopTracks, revokePreview, onDiktatFileChange]);

  useEffect(() => {
    if (!diktatFile) {
      if (phase === 'preview') {
        revokePreview();
        setPhase('idle');
      }
      return;
    }
    if (phase === 'recording') return;
    const url = URL.createObjectURL(diktatFile);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setPhase('preview');
    // Nur bei File-Wechsel; phase absichtlich nicht in deps (Recording-Stop setzt File).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diktatFile]);

  useEffect(
    () => () => {
      clearTimer();
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.ondataavailable = null;
          rec.onstop = null;
          rec.onerror = null;
          rec.stop();
        } catch {
          /* ignore */
        }
      }
      mediaRecorderRef.current = null;
      stopTracks();
      revokePreview();
    },
    [clearTimer, stopTracks, revokePreview],
  );

  const startRecording = useCallback(async () => {
    setMicError(null);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setMicError('Mikrofon im Browser nicht verfügbar (HTTPS oder localhost nötig).');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      streamRef.current = stream;
      const mime = pickRecorderMimeType();
      mimeRef.current = mime;
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = () => {
        setMicError('Aufnahme-Fehler. Bitte erneut versuchen.');
        resetRecording();
      };
      recorder.onstop = () => {
        stopTracks();
        clearTimer();
        const blobMime = mimeRef.current || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobMime });
        chunksRef.current = [];
        if (blob.size === 0) {
          setMicError('Keine Audiodaten aufgenommen.');
          setPhase('idle');
          return;
        }
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const file = new File([blob], `diktat-${stamp}.${extensionForMime(blobMime)}`, {
          type: blobMime,
        });
        onDiktatFileChange(file);
        setPhase('preview');
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setElapsedSec(0);
      setPhase('recording');
      timerRef.current = window.setInterval(() => {
        setElapsedSec((s) => s + 1);
      }, 1000);
    } catch {
      stopTracks();
      setMicError('Mikrofon-Zugriff verweigert oder nicht vorhanden.');
      setPhase('idle');
    }
  }, [onDiktatFileChange, resetRecording, stopTracks, clearTimer]);

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') return;
    try {
      rec.stop();
    } catch {
      setMicError('Stop fehlgeschlagen.');
      resetRecording();
    }
  }, [resetRecording]);

  return (
    <div className="space-y-5">
      <DiktatResultBlocks
        diktatResult={diktatResult}
        onDiktatResultClear={onDiktatResultClear}
        onShowInDokumente={onShowInDokumente}
        dokumenteCta={dokumenteCta}
        onChooseOutputFormat={onChooseOutputFormat}
        formatBusy={formatBusy}
        formatError={formatError}
        formatMenuOpen={formatMenuOpen}
        onFormatMenuOpenChange={onFormatMenuOpenChange}
        intoAkteId={intoAkteId}
        onIntoAkte={onIntoAkte}
        intoAkteBusy={intoAkteBusy}
        intoAkteNote={intoAkteNote}
      />

      {!diktatResult && (
        <div className={`p-4 space-y-3 ${DIKTAT_EDGE}`}>
          <div className="text-center space-y-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mikrofon</p>
            <p className="text-slate-300 text-sm">Direkt im Browser aufnehmen — wie beim Datei-Upload.</p>
          </div>

          {micError && (
            <div className="flex items-start gap-2 text-sm text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{micError}</span>
            </div>
          )}

          {phase === 'idle' && (
            <div className="flex flex-col items-center gap-3 py-2">
              <button
                type="button"
                onClick={() => void startRecording()}
                disabled={diktatUploading}
                className="portal-diktat-standard w-14 h-14 rounded-full bg-violet-600 hover:bg-violet-500 disabled:bg-violet-950 disabled:text-violet-300/40 text-white flex items-center justify-center transition-colors"
                aria-label="Aufnahme starten"
                data-testid="portal-diktat-record-start"
              >
                <Mic className="w-6 h-6" />
              </button>
              <p className="text-slate-400 text-sm">Tippen zum Aufnehmen</p>
            </div>
          )}

          {phase === 'recording' && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-red-500/30 animate-ping" aria-hidden />
                <button
                  type="button"
                  onClick={stopRecording}
                  className="relative w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center"
                  aria-label="Aufnahme stoppen"
                  data-testid="portal-diktat-record-stop"
                >
                  <Square className="w-5 h-5 fill-current" />
                </button>
              </div>
              <p className="text-red-300 text-sm font-medium tabular-nums">
                Aufnahme läuft · {formatDuration(elapsedSec)}
              </p>
              <p className="text-slate-500 text-xs">Stop beendet die Aufnahme und zeigt die Vorschau.</p>
            </div>
          )}

          {phase === 'preview' && diktatFile && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-violet-400/40 bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-5 h-5 text-violet-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">{diktatFile.name}</p>
                  <p className="text-slate-500 text-xs">
                    {(diktatFile.size / 1024).toFixed(0)} KB
                    {elapsedSec > 0 ? ` · ${formatDuration(elapsedSec)}` : ''}
                  </p>
                </div>
              </div>
              {previewUrl && (
                <audio controls src={previewUrl} className="w-full" preload="metadata">
                  Browser unterstützt keine Audio-Vorschau.
                </audio>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={resetRecording}
                  disabled={diktatUploading}
                  className={DIKTAT_ICON}
                  title="Neu aufnehmen"
                  aria-label="Neu aufnehmen"
                >
                  <RotateCcw className="w-3.5 h-3.5" aria-hidden />
                </button>
                <div className="flex-1">
                  <DiktatUploadAction
                    diktatFile={diktatFile}
                    diktatUploading={diktatUploading}
                    onDiktatUpload={onDiktatUpload}
                  />
                </div>
              </div>
            </div>
          )}

          <PrivacyHint />
        </div>
      )}
    </div>
  );
};

const DiktatUploadPanel: React.FC<{
  diktatFile: File | null;
  onDiktatFileChange: (file: File | null) => void;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
  diktatResult: DiktatViewProps['diktatResult'];
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  dokumenteCta: DiktatDokumenteCtaKind;
  onShowAnleitung: () => void;
  onDroppedAudio?: (file: File) => void;
} & DiktatFormatMenuProps> = ({
  diktatFile,
  onDiktatFileChange,
  diktatUploading,
  onDiktatUpload,
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  dokumenteCta,
  onChooseOutputFormat,
  formatBusy,
  formatError,
  formatMenuOpen,
  onFormatMenuOpenChange,
  intoAkteId,
  onIntoAkte,
  intoAkteBusy,
  intoAkteNote,
  onDroppedAudio,
}) => (
  <div className="space-y-5">
    <p className={`${PORTAL_SHELL.textMuted} text-sm`}>
      Audio-Datei wählen (MP3, M4A, WAV, WebM …) — Transkription &amp; Protokoll.
    </p>

    <DiktatResultBlocks
      diktatResult={diktatResult}
      onDiktatResultClear={onDiktatResultClear}
      onShowInDokumente={onShowInDokumente}
      dokumenteCta={dokumenteCta}
      onChooseOutputFormat={onChooseOutputFormat}
      formatBusy={formatBusy}
      formatError={formatError}
      formatMenuOpen={formatMenuOpen}
      onFormatMenuOpenChange={onFormatMenuOpenChange}
      intoAkteId={intoAkteId}
      onIntoAkte={onIntoAkte}
      intoAkteBusy={intoAkteBusy}
      intoAkteNote={intoAkteNote}
    />

    {!diktatResult && (
      <div className={`p-5 space-y-5 ${DIKTAT_EDGE}`}>
        <label className="block">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
            Audio-Datei
          </span>
          <label
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-5 cursor-pointer transition-all ${
              diktatFile ? 'border-violet-400 bg-violet-600/10' : 'border-violet-400/30 hover:border-violet-400/60'
            }`}
            {...diktatAudioDropHandlers((file) => {
              if (onDroppedAudio) onDroppedAudio(file);
              else onDiktatFileChange(file);
            }, diktatUploading)}
          >
            <input
              type="file"
              accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.opus"
              className="hidden"
              onChange={(e) => onDiktatFileChange(e.target.files?.[0] || null)}
            />
            {diktatFile ? (
              <div className="text-center">
                <Headphones className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-white font-medium text-sm">{diktatFile.name}</p>
                <p className="text-slate-500 text-xs mt-1">{(diktatFile.size / 1024 / 1024).toFixed(1)} MB</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onDiktatFileChange(null);
                  }}
                  className={`${DIKTAT_ICON} mt-2 mx-auto`}
                  title="Entfernen"
                  aria-label="Entfernen"
                >
                  <X className="w-3.5 h-3.5" aria-hidden />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <Headphones className="w-5 h-5 text-slate-600 mx-auto mb-1" />
                <p className="text-slate-300 text-sm">Datei ablegen oder wählen</p>
                <p className="text-slate-600 text-xs mt-1">MP3, M4A, WAV · max. 100 MB</p>
                <p
                  className="text-slate-500 text-xs mt-2 leading-relaxed"
                  data-testid="portal-diktat-dropzone-dnd-hint"
                >
                  {DIKTAT_ELEMENT_DND_HINT}
                </p>
              </div>
            )}
          </label>
        </label>

        <DiktatUploadAction
          diktatFile={diktatFile}
          diktatUploading={diktatUploading}
          onDiktatUpload={onDiktatUpload}
        />

        <PrivacyHint />
      </div>
    )}
  </div>
);

const DIKTAT_ELEMENT_HOWTO_STEPS: Array<{
  step: number;
  Icon: typeof Smartphone;
  title: string;
}> = [
  { step: 1, Icon: Smartphone, title: 'Element App installieren' },
  { step: 2, Icon: Link2, title: 'Kanzlei-Raum beitreten' },
  { step: 3, Icon: Mic, title: 'Diktat aufnehmen' },
  { step: 4, Icon: FileCheck, title: 'Protokoll erhalten' },
];

/** Klasse POPOVER_DISMISS_OUTSIDE — How-to nicht als Full-Stack unter dem Chat. */
const DiktatElementHowtoPopover: React.FC<{ onOpenChat: () => void }> = ({ onOpenChat }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const homeserverUrl = portalMatrixHomeserverUrl();
  const onClose = useCallback(() => setOpen(false), []);
  usePopoverDismissOutside({
    open,
    onClose,
    rootRef,
    ignoreCloseRef: triggerRef,
  });

  return (
    <div ref={rootRef} className="relative shrink-0">
      <DiktatRailButton
        ref={triggerRef}
        chrome={false}
        hint={open ? 'Schließen' : 'Element-Anleitung'}
        hintAlign="end"
        type="button"
        data-testid="portal-diktat-element-howto-trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Element-Anleitung"
        onClick={() => setOpen((current) => !current)}
        className="[perspective:280px]"
      >
        <DiktatCube open={open} front={<Info className="w-3.5 h-3.5" aria-hidden />} back={<X className="w-3.5 h-3.5" aria-hidden />} />
      </DiktatRailButton>
      {open ? (
        <div
          role="dialog"
          aria-label="Element-Anleitung"
          data-testid="portal-diktat-element-howto-popover"
          data-popover-dismiss="POPOVER_DISMISS_OUTSIDE"
          className={`absolute right-0 top-full mt-1.5 z-40 w-[min(22rem,calc(100cqw-1.5rem))] max-h-[min(70vh,32rem)] overflow-x-hidden overflow-y-auto custom-scrollbar p-3 space-y-3 text-left ${DIKTAT_EDGE}`}
        >
          {DIKTAT_ELEMENT_HOWTO_STEPS.map(({ step, Icon, title }) => (
            <div key={step} className="flex gap-2.5">
              <div className="w-8 h-8 rounded-lg border border-violet-400/30 bg-[#07070a] flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-slate-400" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 break-words text-left">
                <h3 className="text-white text-sm font-medium m-0">{title}</h3>
                {step === 1 ? (
                  <div className="flex gap-3 mt-2">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=96x96&data=${encodeURIComponent(ELEMENT_DOWNLOAD_URL)}`}
                      alt="QR: Element herunterladen"
                      className="w-24 h-24 shrink-0 rounded-md"
                    />
                    <div className="min-w-0 flex-1 break-words text-left">
                      <a
                        href={ELEMENT_DOWNLOAD_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-violet-300 hover:text-violet-200"
                      >
                        <Link2 className="w-3.5 h-3.5" /> element.io/download
                      </a>
                      <p className="mt-2 text-slate-500 text-xs leading-relaxed m-0">
                        Serveradresse:{' '}
                        <span className="text-slate-400 break-all">{homeserverUrl}</span>
                      </p>
                    </div>
                  </div>
                ) : null}
                {step === 2 ? (
                  <p className="text-slate-400 text-xs mt-1 leading-relaxed m-0">
                    Einladungslink öffnen → in Element beitreten. Ohne Einladung bleibt der Chat hier zu.
                  </p>
                ) : null}
                {step === 3 ? (
                  <p className="text-slate-400 text-xs mt-1 leading-relaxed m-0">
                    Im Browser (Reiter Aufnehmen) oder Sprachnachricht im Raum — Transkript hier, Analyse danach.
                  </p>
                ) : null}
                {step === 4 ? (
                  <p className="text-slate-400 text-xs mt-1 leading-relaxed m-0">
                    PDF und Transkript unter Diktate in diesem Reiter; Analyse danach in ARKO.
                  </p>
                ) : null}
              </div>
            </div>
          ))}
          <p className="text-slate-500 text-xs m-0">
            Fragen?{' '}
            <button type="button" onClick={onOpenChat} className="text-violet-300 hover:text-violet-200">
              ARKO fragen
            </button>
          </p>
        </div>
      ) : null}
    </div>
  );
};

const DiktatAnleitungPanel: React.FC<{
  matrixRoomId?: string;
  elementLoad: ElementDiktatLoadAssessment;
  diktatFile: File | null;
  onDiktatFileChange: (file: File | null) => void;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
  diktatResult: DiktatViewProps['diktatResult'];
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  dokumenteCta: DiktatDokumenteCtaKind;
  side?: React.ReactNode;
} & DiktatFormatMenuProps> = ({
  matrixRoomId = '',
  elementLoad,
  diktatFile,
  onDiktatFileChange,
  diktatUploading,
  onDiktatUpload,
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  dokumenteCta,
  side = null,
  onChooseOutputFormat,
  formatBusy,
  formatError,
  formatMenuOpen,
  onFormatMenuOpenChange,
  intoAkteId,
  onIntoAkte,
  intoAkteBusy,
  intoAkteNote,
}) => {
  const openUrl = elementEmbedSrcForDiktat({
    matrixRoomId,
    loadKind: elementLoad.kind,
  });
  const blocked = elementLoad.kind !== 'manual_file';

  return (
    <div className="w-full min-w-0 flex flex-1 flex-row min-h-0 gap-3 overflow-hidden">
      <div className="flex h-full min-h-0 aspect-[3/4] max-h-full max-w-[calc(100%-12rem)] min-w-0 shrink flex-col gap-2 overflow-hidden">
      <div className="shrink-0">
        <DiktatResultBlocks
          diktatResult={diktatResult}
          onDiktatResultClear={onDiktatResultClear}
          onShowInDokumente={onShowInDokumente}
          dokumenteCta={dokumenteCta}
          onChooseOutputFormat={onChooseOutputFormat}
          formatBusy={formatBusy}
          formatError={formatError}
          formatMenuOpen={formatMenuOpen}
          onFormatMenuOpenChange={onFormatMenuOpenChange}
          intoAkteId={intoAkteId}
          onIntoAkte={onIntoAkte}
          intoAkteBusy={intoAkteBusy}
          intoAkteNote={intoAkteNote}
        />
      </div>

      <div
        className={`flex flex-1 flex-col min-h-0 overflow-hidden ${DIKTAT_EDGE}`}
        data-testid="portal-diktat-element-card"
        data-element-load-kind={elementLoad.kind}
      >
        <div className="flex gap-2 items-start px-3 py-2 shrink-0">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              blocked
                ? 'bg-amber-500/10 border border-amber-500/20'
                : 'bg-violet-600/20 border border-violet-400/40'
            }`}
          >
            {blocked ? (
              <AlertTriangle className="w-4 h-4 text-amber-300" />
            ) : (
              <Smartphone className="w-4 h-4 text-violet-200" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-white text-sm font-medium m-0">{elementLoad.title}</h3>
            {blocked ? (
              <p className="text-slate-400 text-xs mt-1 leading-relaxed m-0">{elementLoad.body}</p>
            ) : null}
          </div>
        </div>
        {openUrl ? (
          <>
            <div className="flex-1 min-h-0 flex flex-col px-2 pb-1">
              <ElementRoomEmbed src={openUrl} testIdPrefix="portal-diktat-element" grow />
            </div>
            <p
              className="px-3 pb-2 text-[11px] text-slate-500 leading-relaxed shrink-0 m-0"
              data-testid="portal-diktat-element-session-lock-hint"
            >
              {DIKTAT_ELEMENT_SESSION_LOCK_HINT}
            </p>
          </>
        ) : (
          <p
            className="text-amber-200/90 text-sm leading-relaxed px-3 pb-3 m-0"
            data-testid="portal-diktat-element-honest-block"
          >
            {elementLoad.kind === 'blocked_no_room'
              ? 'Kein Kanzlei-Raum hinterlegt — der Chat bleibt hier zu. Aufnahme und Datei-Upload gehen weiter.'
              : elementLoad.kind === 'blocked_no_homeserver'
                ? 'Der Chat-Server ist gerade nicht erreichbar — der Kanzlei-Raum bleibt zu. Aufnahme und Datei-Upload gehen weiter.'
                : 'Der Chat ist in dieser Umgebung nicht eingebettet. Aufnahme und Datei unter Diktat bleiben möglich.'}
          </p>
        )}
      </div>
      </div>

      <div className="flex min-w-[11rem] flex-1 min-h-0 flex-col gap-3 overflow-y-auto custom-scrollbar">
      {!diktatResult && elementLoad.canPickAudioFile ? (
        <div className={`px-3 py-2 space-y-2 shrink-0 ${DIKTAT_EDGE}`}>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider m-0">
            Audio aus Element laden
          </p>
          <p
            className={`${PORTAL_SHELL.textMuted} text-xs leading-relaxed m-0`}
            data-testid="portal-diktat-element-dnd-hint"
          >
            {DIKTAT_ELEMENT_DND_HINT} Mikrofon und Datei-Upload bleiben unverändert.
          </p>
          <label
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg px-2 py-2 cursor-pointer transition-all ${
              diktatFile ? 'border-violet-400 bg-violet-600/10' : 'border-violet-400/30 hover:border-violet-400/60'
            }`}
            {...diktatAudioDropHandlers(onDiktatFileChange, diktatUploading)}
          >
            <input
              type="file"
              accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.opus"
              className="hidden"
              data-testid="portal-diktat-element-file"
              onChange={(e) => onDiktatFileChange(e.target.files?.[0] || null)}
            />
            {diktatFile ? (
              <div className="text-center">
                <Headphones className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-white font-medium text-sm m-0">{diktatFile.name}</p>
                <p className="text-slate-500 text-xs mt-0.5 m-0">{(diktatFile.size / 1024).toFixed(0)} KB</p>
              </div>
            ) : (
              <div className="text-center">
                <Smartphone className="w-5 h-5 text-slate-600 mx-auto mb-1" />
                <p className="text-slate-300 text-sm m-0">Exportierte Sprachnachricht wählen</p>
                <p className="text-slate-600 text-xs mt-0.5 m-0">MP3, M4A, WAV, WebM · wie Aufnahme und Upload</p>
              </div>
            )}
          </label>
          <DiktatUploadAction
            diktatFile={diktatFile}
            diktatUploading={diktatUploading}
            onDiktatUpload={onDiktatUpload}
          />
        </div>
      ) : null}
      {side}
      </div>
    </div>
  );
};

function archiveRowLabel(entry: DiktatArchiveEntry): string {
  return entry.filename || entry.sourceName || 'Diktat';
}

function archiveRowTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function archiveSnippet(text: string): string {
  const compact = text.replace(/\s+/g, ' ').trim();
  if (compact.length <= 96) return compact;
  return `${compact.slice(0, 96).trim()}…`;
}

const DiktatArchiveFolder: React.FC<{
  archive: DiktatArchiveEntry[];
  selectedId: string | null;
  onSelect?: (id: string) => void;
  protokolle: PortalProtokoll[];
  akten: PortalAkteRecord[];
  canWriteDocuments: boolean;
  isPremium: boolean;
  authToken: string | null;
  customerId: string;
  kanzleiId: string;
  matrixRoomId?: string;
  customerStatus?: string;
  customerEmail?: string;
  protokolleLoading: boolean;
  onRefreshDocuments?: () => void | Promise<void>;
  onOpenDocument?: (fields: PortalDocumentOpenFields) => void;
  onChooseOutputFormat?: (format: DiktatOutputFormat, source: 'result' | string) => void;
  formatBusy?: boolean;
  formatError?: string | null;
  formatMenuKey?: string | null;
  onFormatMenuKeyChange?: (key: string | null) => void;
}> = ({
  archive,
  selectedId,
  onSelect,
  protokolle,
  akten,
  canWriteDocuments,
  isPremium,
  authToken,
  customerId,
  kanzleiId,
  matrixRoomId = '',
  customerStatus,
  customerEmail,
  protokolleLoading,
  onRefreshDocuments,
  onOpenDocument,
  onChooseOutputFormat,
  formatBusy = false,
  formatError = null,
  formatMenuKey = null,
  onFormatMenuKeyChange,
}) => {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [moveDocId, setMoveDocId] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const moveLockRef = useRef(false);
  const [rowMsg, setRowMsg] = useState<string | null>(null);
  const [sharePopover, setSharePopover] = useState<{
    docId: string;
    left: number;
    top: number;
  } | null>(null);
  const shareAnchorRef = useRef<HTMLElement | null>(null);
  const [docShares, setDocShares] = useState<PortalDocumentShare[]>([]);
  const [kanzleiSeats, setKanzleiSeats] = useState<KanzleiSeatForAktePicker[]>([]);

  const closeSharePopover = useCallback(() => {
    shareAnchorRef.current = null;
    setSharePopover(null);
  }, []);

  const refreshSitzePrivacy = useCallback(async () => {
    const token = authToken?.trim();
    if (!token) {
      setDocShares([]);
      setKanzleiSeats([]);
      return;
    }
    const [shares, seats] = await Promise.all([
      listPortalDocumentShares(token),
      kanzleiId ? listKanzleiSeatsForAktePicker(token, kanzleiId) : Promise.resolve([]),
    ]);
    setDocShares(shares);
    setKanzleiSeats(seats);
  }, [authToken, kanzleiId]);

  useEffect(() => {
    void refreshSitzePrivacy();
  }, [customerId, refreshSitzePrivacy]);

  const protoById = useCallback(
    (pbId?: string) => {
      const id = String(pbId || '').trim();
      if (!id) return undefined;
      return protokolle.find((row) => row.id === id);
    },
    [protokolle],
  );

  const rowDoc = useCallback(
    (entry: DiktatArchiveEntry): PortalProtokoll | undefined => {
      return protoById(entry.pb_record_id) ?? protokollFromDiktatArchiveEntry(entry);
    },
    [protoById],
  );

  const openEntry = useCallback(
    (entry: DiktatArchiveEntry) => {
      onSelect?.(entry.id);
      const doc = rowDoc(entry);
      const url = doc?.nc_url || entry.pdfUrl;
      if (!url) {
        setRowMsg('Öffnen nicht möglich — kein Dokumentlink.');
        return;
      }
      if (doc && canOpenDocumentInPortal(isPremium, doc.filename, doc.nextcloud_path, doc.nc_url)) {
        onOpenDocument?.(portalDocumentOpenFieldsFromProtokoll(doc));
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [isPremium, onOpenDocument, onSelect, rowDoc],
  );

  const editEntry = useCallback(
    (entry: DiktatArchiveEntry) => {
      onSelect?.(entry.id);
      const doc = rowDoc(entry);
      const path = doc?.nextcloud_path || entry.nextcloud_path;
      const filename = doc?.filename || entry.filename || entry.sourceName;
      const openable = Boolean(
        doc && canOpenDocumentInPortal(isPremium, doc.filename, doc.nextcloud_path, doc.nc_url),
      );
      const action = diktatEditAction({
        nextcloudPath: path,
        filename,
        openableInEditor: openable,
      });
      if (action === 'pick_format') {
        onFormatMenuKeyChange?.(formatMenuKey === entry.id ? null : entry.id);
        return;
      }
      if (action === 'blocked' || !doc) {
        setRowMsg(
          diktatHasAllowedEditPath(path)
            ? 'Bearbeiten nicht möglich — Format oder Dokumentlink fehlt.'
            : 'Bearbeiten nicht möglich — Datei ist nicht in der Ablage.',
        );
        return;
      }
      if (!onOpenDocument) {
        setRowMsg('Bearbeiten nicht möglich — Editor ist hier nicht angebunden.');
        return;
      }
      onOpenDocument(portalDocumentOpenFieldsFromProtokoll(doc));
    },
    [formatMenuKey, isPremium, onFormatMenuKeyChange, onOpenDocument, onSelect, rowDoc],
  );

  const archiveEntry = useCallback(
    async (entry: DiktatArchiveEntry) => {
      const pbId = String(entry.pb_record_id || '').trim();
      if (!pbId) {
        setRowMsg('Löschen braucht einen gespeicherten Eintrag.');
        return;
      }
      setArchivingId(pbId);
      try {
        const result = await archivePortalDocumentRecord(pbId);
        if (result.ok) {
          setDeleteConfirmId(null);
          await Promise.resolve(onRefreshDocuments?.());
        } else {
          setRowMsg(result.error);
        }
      } finally {
        setArchivingId(null);
      }
    },
    [onRefreshDocuments],
  );

  const assignToAkte = useCallback(
    async (doc: PortalProtokoll, akte: PortalAkteRecord) => {
      if (!canWriteDocuments) {
        const msg = 'Zuordnen nur mit Schreibrecht.';
        setRowMsg(msg);
        setMoveError(msg);
        return;
      }
      if (!doc.nextcloud_path?.trim()) {
        const msg = 'Dokument hat keinen Ablageort — Zuordnung nicht möglich.';
        setRowMsg(msg);
        setMoveError(msg);
        return;
      }
      const token = authToken?.trim() || '';
      if (!token) {
        const msg = 'Bitte neu anmelden.';
        setRowMsg(msg);
        setMoveError(msg);
        return;
      }
      const targetFolder = portalAkteAssignTargetFolder(akte.id);
      if (!targetFolder) {
        setRowMsg('Akte braucht einen Ordnernamen — bitte umbenennen und erneut zuordnen.');
        setMoveError('Akte braucht einen Ordnernamen — bitte umbenennen und erneut zuordnen.');
        return;
      }
      if (moveLockRef.current) return;
      moveLockRef.current = true;
      setMoveBusy(true);
      setMoveError(null);
      try {
        await createPortalFolderViaApi({
          customerId,
          folderPath: targetFolder,
          customerStatus,
          customerEmail,
        });
        const moved = await movePortalDocumentViaFolderApi({
          customerId,
          customerStatus,
          customerEmail,
          pbRecordId: doc.id,
          sourcePath: doc.nextcloud_path,
          targetFolderPath: targetFolder,
        });
        const canonicalAktePath = portalAkteNcPathFromMovedDocument(
          moved.nextcloud_path,
          akte.id,
        );
        if (!canonicalAktePath || moved.akte_id !== akte.id) {
          setRowMsg('Verschieben konnte der Ziel-Akte nicht bestätigt werden.');
          return;
        }
        const saved = await updatePortalAkteNcPath(token, akte.id, canonicalAktePath);
        if (saved.ok === false) {
          setRowMsg(saved.error);
          return;
        }
        setMoveDocId(null);
        setMoveError(null);
        setRowMsg(`„${doc.filename}" zugeordnet — Akte „${saved.data.title}"`);
        await Promise.resolve(onRefreshDocuments?.());
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setMoveError(msg);
        setRowMsg(msg);
      } finally {
        moveLockRef.current = false;
        setMoveBusy(false);
      }
    },
    [
      authToken,
      canWriteDocuments,
      customerEmail,
      customerId,
      customerStatus,
      onRefreshDocuments,
    ],
  );

  const shareDoc = sharePopover
    ? protoById(sharePopover.docId) ??
      archive
        .map((row) => protokollFromDiktatArchiveEntry(row))
        .find((row) => row?.id === sharePopover.docId) ??
      null
    : null;
  const moveDoc = moveDocId
    ? protoById(moveDocId) ??
      archive.map((row) => protokollFromDiktatArchiveEntry(row)).find((row) => row?.id === moveDocId)
    : undefined;

  return (
    <section
      className={`overflow-visible ${DIKTAT_EDGE}`}
      data-testid="portal-diktat-archive"
      aria-label="Diktate"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-violet-400/20">
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
          <span className="font-semibold text-slate-300 text-[12px]">Diktate</span>
        </div>
        <span className="text-[10px] text-slate-500">
          {protokolleLoading && archive.length === 0
            ? 'lädt…'
            : diktatArchiveSourceLabel({ archive, loading: protokolleLoading })}
        </span>
      </div>
      {rowMsg ? (
        <p className="m-0 px-3 py-2 text-[11px] text-amber-200/90 border-b border-violet-400/20">{rowMsg}</p>
      ) : null}
      {archive.length === 0 && !protokolleLoading ? (
        <p className="m-0 px-3 py-3 text-[12px] text-slate-500" data-testid="portal-diktat-archive-empty">
          Noch keine Aufnahmen in diesem Ordner. Transkripte liegen unter Diktate — dieselbe Quelle wie
          unter Dokumente. Sprachnachrichten aus Element kommen nicht still (Ende-zu-Ende-Verschlüsselung).
        </p>
      ) : (
      <ul className="divide-y divide-violet-400/20 m-0 p-0 list-none">
        {archive.map((entry) => {
          const selected = selectedId === entry.id;
          const doc = rowDoc(entry);
          const confirmingDelete = deleteConfirmId === entry.id;
          const openable = doc
            ? canOpenDocumentInPortal(isPremium, doc.filename, doc.nextcloud_path, doc.nc_url)
            : Boolean(entry.pdfUrl);
          const hasLink = Boolean(doc?.nc_url || entry.pdfUrl);
          const filename = doc?.filename || entry.filename || entry.sourceName;
          const gate = diktatExplorerRowGate({
            canWrite: canWriteDocuments,
            hasPbDoc: Boolean(doc),
            nextcloudPath: doc?.nextcloud_path || entry.nextcloud_path,
            filename,
            documentUrl: doc?.nc_url || entry.pdfUrl,
            pbRecordId: entry.pb_record_id || doc?.id,
            openableInEditor: openable,
          });
          const editAction = diktatEditAction({
            nextcloudPath: doc?.nextcloud_path || entry.nextcloud_path,
            filename,
            openableInEditor: openable,
          });
          return (
            <li key={entry.id} className={selected ? 'bg-violet-500/10' : ''}>
              <div className="px-3 py-2.5 flex items-start gap-2.5">
                <button
                  type="button"
                  onClick={() => onSelect?.(entry.id)}
                  className="min-w-0 flex-1 text-left bg-transparent border-0 p-0 m-0 cursor-pointer hover:bg-violet-600/10 rounded-md"
                  data-testid="portal-diktat-archive-item"
                >
                  <span className="flex items-start gap-2.5">
                    <FileText className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-sm text-white truncate">{archiveRowLabel(entry)}</span>
                        <span className="text-[10px] text-slate-500 flex-shrink-0 tabular-nums">
                          {archiveRowTime(entry.createdAt)}
                        </span>
                      </span>
                      <span className="block text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                        {entry.transcriptText.trim()
                          ? archiveSnippet(entry.transcriptText)
                          : entry.pb_record_id
                            ? 'Gespeicherte Datei — Transkript nur in dieser Sitzung sichtbar.'
                            : 'Transkript dieser Sitzung — noch nicht gespeichert.'}
                      </span>
                    </span>
                  </span>
                </button>
                {confirmingDelete ? (
                  <div className="flex items-center gap-1 rounded-md bg-red-500/10 px-1.5 py-0.5 flex-shrink-0">
                    <span className="text-[10px] text-red-200">Löschen?</span>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(null)}
                      className="text-[10px] text-slate-400 hover:text-slate-200 px-1"
                    >
                      Nein
                    </button>
                    <button
                      type="button"
                      onClick={() => void archiveEntry(entry)}
                      disabled={!canWriteDocuments || archivingId === entry.pb_record_id}
                      className="text-[10px] text-red-200 hover:text-white px-1"
                    >
                      Ja
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start gap-0.5 flex-shrink-0">
                    {onChooseOutputFormat ? (
                      <DiktatOutputFormatMenu
                        compact
                        open={formatMenuKey === entry.id}
                        busy={formatBusy}
                        error={formatMenuKey === entry.id ? formatError : null}
                        onOpenChange={(open) => onFormatMenuKeyChange?.(open ? entry.id : null)}
                        onChoose={(format) => onChooseOutputFormat(format, entry.id)}
                      />
                    ) : null}
                  <DokumenteRowActions
                    onOpen={() => openEntry(entry)}
                    onEdit={() => editEntry(entry)}
                    onShare={
                      doc
                        ? (e) => {
                            const btn = e.currentTarget;
                            if (sharePopover?.docId === doc.id) {
                              closeSharePopover();
                              return;
                            }
                            shareAnchorRef.current = btn;
                            const pos = placeSharePopover(btn.getBoundingClientRect(), {
                              width: window.innerWidth,
                              height: window.innerHeight,
                            });
                            setSharePopover({ docId: doc.id, ...pos });
                          }
                        : undefined
                    }
                    shareOpen={Boolean(doc && sharePopover?.docId === doc.id)}
                    onMoveToAkte={doc ? () => setMoveDocId(doc.id) : undefined}
                    onDelete={() => setDeleteConfirmId(entry.id)}
                    openDisabled={gate.openDisabled}
                    openHint={
                      openable
                        ? 'Öffnen'
                        : hasLink || doc?.nextcloud_path || entry.nextcloud_path
                          ? 'Öffnen (externer Link)'
                          : 'Öffnen nicht möglich — kein Dokumentlink'
                    }
                    editDisabled={gate.editDisabled}
                    editHint={
                      gate.editDisabled
                        ? diktatHasAllowedEditPath(doc?.nextcloud_path || entry.nextcloud_path)
                          ? 'Bearbeiten nicht möglich — Format oder Dokumentlink fehlt'
                          : 'Bearbeiten braucht eine Datei in der Ablage'
                        : editAction === 'pick_format'
                          ? 'Format wählen (TXT nicht in OnlyOffice)'
                          : editAction === 'pdf_viewer'
                            ? 'Im Viewer öffnen'
                            : 'Bearbeiten'
                    }
                    shareDisabled={gate.shareDisabled}
                    shareHint="Teilen · wer sieht das"
                    moveToAkteDisabled={gate.moveToAkteDisabled}
                    moveToAkteHint={
                      gate.moveToAkteDisabled
                        ? 'Zuordnen braucht Schreibrecht und einen gespeicherten Eintrag'
                        : 'Akte zuordnen…'
                    }
                    deleteDisabled={gate.deleteDisabled}
                    deleteHint={
                      gate.deleteDisabled
                        ? 'Löschen nur mit Schreibrecht und gespeichertem Eintrag'
                        : 'Aus der Liste entfernen (Datei in der Ablage bleibt)'
                    }
                    deleteBusy={archivingId === entry.pb_record_id}
                  />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      )}
      {shareDoc && sharePopover && authToken ? (
        <PortalDocumentSharePanel
          doc={shareDoc}
          viewerId={customerId}
          kanzleiId={kanzleiId || ''}
          authToken={authToken}
          canShare={canWriteDocuments && Boolean(kanzleiId)}
          seats={kanzleiSeats}
          shares={docShares}
          matrixRoomId={matrixRoomId}
          anchor={{ left: sharePopover.left, top: sharePopover.top }}
          ignoreCloseRef={shareAnchorRef}
          onClose={closeSharePopover}
          onSharesChanged={refreshSitzePrivacy}
        />
      ) : null}
      {moveDocId ? (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setMoveDocId(null)}
          role="presentation"
        >
          <div
            className={`w-full max-w-md p-4 space-y-3 shadow-2xl ${DIKTAT_EDGE}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="diktat-move-to-akte-title"
          >
            <h3 id="diktat-move-to-akte-title" className="text-sm font-semibold text-white m-0">
              In Akte verschieben
            </h3>
            {!moveDoc?.nextcloud_path?.trim() ? (
              <p className="text-xs text-slate-400 m-0">
                Zuordnen braucht eine Datei in der Ablage — wie im Explorer, ohne geratene Akte.
              </p>
            ) : akten.length === 0 ? (
              <p className="text-xs text-slate-400 m-0">
                Noch keine Akte. Anlegen im Reiter Akten — optional, die Ablage bleibt nutzbar.
              </p>
            ) : (
              <ul className="max-h-64 overflow-y-auto space-y-1 m-0 p-0 list-none">
                {akten.map((akteRow) => (
                  <li key={akteRow.id}>
                    <button
                      type="button"
                      className={DIKTAT_QUIET}
                      disabled={moveBusy}
                      onClick={() => {
                        if (moveDoc) void assignToAkte(moveDoc, akteRow);
                      }}
                    >
                      {akteRow.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {moveError ? (
              <p className="m-0 text-[11px] text-amber-100" role="alert" data-testid="portal-diktat-move-error">
                {moveError}
              </p>
            ) : null}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setMoveDocId(null);
                  setMoveError(null);
                }}
                disabled={moveBusy}
                className={`${DIKTAT_QUIET} w-auto`}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

type DiktatPaneBoundaryProps = { children: React.ReactNode };
type DiktatPaneBoundaryState = { failed: boolean };

const DiktatErrorShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mark = portalDiktatSurfaceClass(usePortalAppearance());
  return (
    <div
      className={`w-full min-w-0 max-w-none flex-1 overflow-y-auto space-y-3 min-h-0 px-3 py-3 ${DIKTAT_GROUND} ${mark}`}
      data-testid="portal-diktat-dropzone"
      data-layout="pane-fit"
      data-diktat-pane="scroll-or-grow"
    >
      {children}
    </div>
  );
};

/** Ein Renderfehler lässt den Kopf stehen. Kein erfundenes Transkript. */
export class DiktatPaneBoundary extends React.Component<DiktatPaneBoundaryProps, DiktatPaneBoundaryState> {
  state: DiktatPaneBoundaryState = { failed: false };

  static getDerivedStateFromError(): DiktatPaneBoundaryState {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <DiktatErrorShell>
        <DiktatKopf activeTab="record" />
        <p role="alert" data-testid="portal-diktat-pane-error" className="text-sm text-amber-100">
          {DIKTAT_PANE_ERROR}
        </p>
        <button
          type="button"
          data-testid="portal-diktat-pane-retry"
          onClick={() => this.setState({ failed: false })}
          className={DIKTAT_BTN}
        >
          Erneut anzeigen
        </button>
      </DiktatErrorShell>
    );
  }
}
